
/**
 * MapperInspector — right pane that edits the currently-selected column's
 * Rule.
 *
 * For every rule kind the editor surfaces the same {{ ... }} autocomplete
 * affordance (where it makes sense). Below the editor we show a tiny preview
 * of the column's expanded value for the first three source rows.
 */

import { useEffect, useMemo, useState } from "react";
import { getModuleUsage, getSettings } from "@/lib/api/endpoints";
import { resolveTemplate } from "@/modules/_shared/templateValue";
import { defaultHelpers } from "@/lib/mapper/helpers";
import { AI_RULE_PLACEHOLDER } from "@/lib/mapper/mapping";
import { cellModule, pickableCellModules } from "@/lib/mapper/modules";
import {
  analyzeDependencies,
  extractOutputRefs,
} from "@/lib/mapper/dependencies";
import type {
  LookupDictionary,
  ProductType,
  Profile,
  Rule,
  SourceRow,
  ShopifyRow,
} from "@/lib/mapper/types";
import { presetsForColumn, type AIPreset } from "./aiPresets";
import {
  columnStatusFor,
  TONE_CLASSES,
  type ColumnStatus,
} from "./columnStatus";
import { NA_BY_DEFAULT } from "@/lib/mapper/columns";
import { localClaudeCapability } from "@/modules/ai/providers";
import { moduleForColumn, type Module } from "@/lib/mapper/modules";
import {
  FALLBACK_DEFAULT_MODELS,
  type AccessibleModelsMap,
  type DefaultModels,
} from "@/modules/ai/shared";
import type { FieldInputHandle } from "@/modules/_shared/FieldInput.client";
import { ModuleIconView } from "@/components/icons/ModuleIconView";
import { ModuleEditorHost } from "./_inspector/ModuleEditorHost";
import { shortString } from "@/modules/_shared/format";

type RuleKind = Rule["kind"];

// Picker entries derive from the module catalog (single source of truth for
// kind order, labels, and hints). See src/lib/mapper/modules/catalog.ts.
const KINDS: { value: RuleKind; label: string; hint: string }[] = (() => {
  const seen = new Set<string>();
  const out: { value: RuleKind; label: string; hint: string }[] = [];
  for (const m of pickableCellModules()) {
    if (seen.has(m.kind)) continue;
    seen.add(m.kind);
    const label = m.kind === "ai" ? "AI" : m.name;
    out.push({ value: m.kind as RuleKind, label, hint: m.tooltip });
  }
  return out;
})();

// Fresh default instance for a kind. Delegates to the catalog so the default
// shape lives in one place (was a local switch). The "ai" default still resolves
// the fallback model + reasoning exactly as before.
function emptyRule(kind: RuleKind): Rule {
  return cellModule(kind)!.defaultConfig();
}

/**
 * The row of module "pills" used to PICK or CHANGE the module for a column.
 * Extracted so it can render both in the full inspector and inside the embedded
 * module drawer (where the picker used to be hidden, leaving no clear way to
 * switch modules).
 */
function KindPickerRow({
  sortedKinds,
  activeKind,
  usage,
  onPick,
}: {
  sortedKinds: { value: RuleKind; label: string; hint: string }[];
  activeKind: RuleKind;
  usage: Record<string, number>;
  onPick: (kind: RuleKind) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {sortedKinds.map((k) => {
        const active = activeKind === k.value;
        const isAI = k.value === "ai";
        const meta = cellModule(k.value);
        const count = usage[k.value] ?? 0;
        return (
          <button
            key={k.value}
            type="button"
            onClick={() => onPick(k.value)}
            title={k.hint}
            className={[
              "inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium font-mono transition",
              active
                ? isAI
                  ? "bg-violet-600 text-white shadow-sm"
                  : "bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-950 text-white shadow-sm"
                : isAI
                  ? "text-violet-600 dark:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-950/40 hover:text-violet-800"
                  : "text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-surface-2 hover:text-zinc-900 dark:hover:text-zinc-100",
            ].join(" ")}
          >
            {meta && (
              <ModuleIconView icon={meta.icon} size={12} className="shrink-0" />
            )}
            <span>{k.label}</span>
            {count > 0 && (
              <span
                title={`Used ${count} time(s) across all templates`}
                className={[
                  "ml-0.5 shrink-0 rounded px-1 py-px text-[9px] font-semibold leading-none ring-1",
                  active
                    ? "bg-white/15 text-white ring-white/20"
                    : "bg-slate-100 dark:bg-surface-2 text-slate-600 dark:text-slate-400 ring-slate-200 dark:ring-slate-700",
                ].join(" ")}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

type Props = {
  column: string;
  rule: Rule | undefined;
  /** Used to specialise AI quick-start presets per product type. */
  productType: ProductType;
  sourceHeaders: string[];
  sourceRows: SourceRow[];
  /** Generated preview rows for the entire profile (used to show the actual evaluated value). */
  previewRows: ShopifyRow[];
  previewLoading: boolean;
  /**
   * The full active profile — used to feed `{{out:...}}` autocomplete and to
   * surface dependency / cycle chips for the selected column.
   */
  profile: Profile;
  onChangeRule: (next: Rule) => void;
  onClearRule: () => void;
  /** Per-(column,kind) memory so the editor preserves what the user typed when they flip kinds. */
  kindMemory: Record<string, Rule>;
  onRememberKind: (key: string, rule: Rule) => void;
  /** Lets the parent route source-header clicks here. */
  registerFocusedAutocomplete: (
    handle: FieldInputHandle | null,
  ) => void;
  /** Navigate the inspector to another column (used by dep chips). */
  onSelectColumn?: (column: string) => void;
  /** When true, hides the column header + kind pills (the host drawer
   *  supplies its own browse UI). Editor + inline preview still render. */
  embedded?: boolean;
  /** Slug-keyed dictionary map for inline Dictionary module preview. */
  dictionaries?: Record<string, LookupDictionary>;
  /** Called after a dictionary entry is saved inline, so the page can refresh its dictionaryMap. */
  onDictionariesChanged?: () => void;
};

export function MapperInspector({
  column,
  rule,
  productType,
  sourceHeaders,
  sourceRows,
  previewRows,
  previewLoading,
  profile,
  onChangeRule,
  onClearRule,
  kindMemory,
  onRememberKind,
  registerFocusedAutocomplete,
  onSelectColumn,
  embedded = false,
  dictionaries,
  onDictionariesChanged,
}: Props) {
  // Output columns are every Shopify column that already has a rule in the
  // active profile. Computed once per render — cheap (just Object.keys).
  const outputColumns = useMemo(
    () => Object.keys(profile.fields),
    [profile.fields],
  );
  // Re-run dependency analysis so we can flag cycle membership for the
  // selected column inline. (The drawer Sequence panel uses the same analysis
  // — different render path, different cache, but identical input.)
  const dependencyAnalysis = useMemo(
    () => analyzeDependencies(profile),
    [profile],
  );
  const cycleColumns = useMemo(() => {
    const set = new Set<string>();
    for (const cycle of dependencyAnalysis.cycles) {
      for (const c of cycle) set.add(c);
    }
    return set;
  }, [dependencyAnalysis.cycles]);
  // Fetch the user-configured default models + per-provider accessible-model
  // info once per inspector mount. We do this here (rather than in the parent
  // page) so the inspector stays self-contained — the parent already has
  // plenty of state and the request is cheap and cached behind the API.
  const [defaultModels, setDefaultModels] = useState<DefaultModels>(
    FALLBACK_DEFAULT_MODELS,
  );
  const [accessibleModels, setAccessibleModels] = useState<AccessibleModelsMap>(
    {},
  );
  // Backend capability: local Claude Code (AI_LOCAL_CLI=true). Off by default
  // so hosted deployments never offer the provider.
  const [localClaude, setLocalClaude] = useState(false);
  const [settingsRefreshTick, setSettingsRefreshTick] = useState(0);
  const [usage, setUsage] = useState<Record<string, number>>({});

  useEffect(() => {
    let cancelled = false;
    getSettings()
      .then((json) => {
        if (cancelled || !json?.settings) return;
        setLocalClaude(localClaudeCapability(json));
        // Settings come back with JSON values intact (strings stay strings,
        // objects stay objects). `defaultModel` is a string; `accessibleModels`
        // is the structured shape persisted by the Test Connection action.
        const s = json.settings as Record<string, unknown>;
        const readString = (k: string): string | undefined => {
          const v = s[k];
          return typeof v === "string" ? v : undefined;
        };
        setDefaultModels({
          anthropic:
            readString("v2.ai.anthropic.defaultModel") ||
            FALLBACK_DEFAULT_MODELS.anthropic,
          openai:
            readString("v2.ai.openai.defaultModel") ||
            FALLBACK_DEFAULT_MODELS.openai,
          "claude-local":
            readString("v2.ai.claude-local.defaultModel") ||
            FALLBACK_DEFAULT_MODELS["claude-local"],
          openrouter:
            readString("v2.ai.openrouter.defaultModel") ||
            FALLBACK_DEFAULT_MODELS.openrouter,
        });
        const next: AccessibleModelsMap = {};
        for (const provider of ["anthropic", "openai", "claude-local", "openrouter"] as const) {
          const raw = s[`v2.ai.${provider}.accessibleModels`];
          if (raw && typeof raw === "object" && !Array.isArray(raw)) {
            const obj = raw as Record<string, unknown>;
            if (typeof obj.ok === "boolean" && Array.isArray(obj.models)) {
              next[provider] = {
                ok: obj.ok,
                models: obj.models.filter(
                  (m): m is string => typeof m === "string",
                ),
                checkedAt:
                  typeof obj.checkedAt === "string" ? obj.checkedAt : "",
                error: typeof obj.error === "string" ? obj.error : undefined,
              };
            }
          }
        }
        setAccessibleModels(next);
      })
      .catch(() => {
        /* non-fatal — keep fallbacks */
      });
    return () => {
      cancelled = true;
    };
  }, [settingsRefreshTick]);

  useEffect(() => {
    let live = true;
    getModuleUsage()
      .then((d) => {
        if (live) setUsage(d.usage ?? {});
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  const refreshSettings = () => setSettingsRefreshTick((n) => n + 1);

  const sortedKinds = useMemo(
    () =>
      KINDS.map((kind, index) => ({ kind, index }))
        .sort((a, b) => {
          const delta =
            (usage[b.kind.value] ?? 0) - (usage[a.kind.value] ?? 0);
          return delta || a.index - b.index;
        })
        .map(({ kind }) => kind),
    [usage],
  );

  // The "active" rule the editor shows. When no rule exists yet, default to a
  // blank `static` rule so the editor renders something useful (this is the
  // fix for the Product Category bug: the column is selectable AND editable
  // even before a rule exists).
  const activeRule: Rule = rule ?? emptyRule("static");
  const hasPersistedRule = !!rule;

  function changeKind(kind: RuleKind) {
    const memKey = `${column}::${kind}`;
    const remembered = kindMemory[memKey];
    const next = remembered ?? emptyRule(kind);
    onChangeRule(next);
  }

  function mutate(next: Rule) {
    onChangeRule(next);
    onRememberKind(`${column}::${next.kind}`, next);
  }

  // ---- AI Quick-Start Presets --------------------------------------------
  // Surface the "Use AI for this" affordance when we have a preset for the
  // selected column AND the user isn't already editing an AI rule. The card
  // builds a fresh AI rule from the preset and routes it through the same
  // onChangeRule pipeline the segmented control uses.
  const columnPresets = useMemo(() => presetsForColumn(column), [column]);
  const showPresetCard =
    columnPresets.length > 0 && activeRule.kind !== "ai";

  function applyPreset(preset: AIPreset) {
    const next = preset.build(productType);
    // Memo so that flipping the kind picker away and back keeps the prefill.
    onRememberKind(`${column}::ai`, next);
    onChangeRule(next);
  }

  // Module-awareness: when the selected column is owned by a Module, surface
  // a tone-coloured band under the kind picker. If the module is authoritative
  // (e.g. Image Fanout writes the column wholesale), we also suppress the rule
  // editor below — a per-cell rule would be ignored.
  const ownedModule = useMemo(
    () => moduleForColumn(profile, column),
    [profile, column],
  );
  // The column's overall status — drives the header dot. We pass NA_BY_DEFAULT
  // through `columnStatusFor` so the dot logic stays identical to the sidebar.
  const headerStatus: ColumnStatus = useMemo(
    () => columnStatusFor(profile, column, NA_BY_DEFAULT),
    [profile, column],
  );
  const hideRuleEditor = !!ownedModule?.authoritative;
  // Inside the module drawer the drawer header already names the column and
  // module, so this band only renders when it has something extra to say.
  const showHeader = !embedded || !!ownedModule || showPresetCard;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Header — column identity + kind picker + (optional) module band. */}
      {showHeader && (
      <div className="border-b border-zinc-200/80 px-5 py-4">
        {!embedded && (
          <>
        {/* Row 1: status dot · column name · clear icon */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <HeaderStatusDot status={headerStatus} />
            <span
              className="min-w-0 break-words font-mono text-[15px] font-semibold leading-tight text-slate-900 dark:text-slate-100"
              title={column}
            >
              {column}
            </span>
            {activeRule.kind === "ai" && (
              <span className="shrink-0 rounded bg-violet-100 dark:bg-violet-950/50 px-1.5 py-px font-sans text-[9.5px] font-bold uppercase tracking-wide text-violet-800 dark:text-violet-300 ring-1 ring-violet-200 dark:ring-violet-800">
                AI
              </span>
            )}
          </div>
          {hasPersistedRule && (
            <button
              type="button"
              onClick={onClearRule}
              title="Clear this rule"
              aria-label="Clear this rule"
              className="shrink-0 rounded border border-slate-200 dark:border-border px-1.5 py-1 text-[12px] leading-none text-slate-500 dark:text-slate-400 transition hover:border-red-300 dark:hover:border-red-700 hover:bg-red-50 dark:hover:bg-red-950/40 hover:text-red-700 dark:hover:text-red-300"
            >
              <span aria-hidden="true">↩</span>
            </button>
          )}
        </div>

        {/* Row 2: compact kind pills — pick / change the module */}
        <div className="mt-3">
          <KindPickerRow
            sortedKinds={sortedKinds}
            activeKind={activeRule.kind}
            usage={usage}
            onPick={changeKind}
          />
        </div>

          </>
        )}

        {/* The module drawer intentionally omits a "Change module" picker — the
            ← (clear) control in the header's top-right is the way back out. */}
        {/* Module band — shown when the column is owned by an active module.
            For authoritative modules we explicitly tell the user the rule
            editor below will be ignored. */}
        {ownedModule && (
          <ModuleBand
            module={ownedModule}
            profile={profile}
            authoritative={!!ownedModule.authoritative}
          />
        )}

        {/* AI Quick-Start preset card. Sits below the kind picker so a
            non-technical user can one-click into an AI rule without first
            understanding the kind taxonomy. Disappears the moment the rule
            becomes AI, and re-appears if the user flips the kind back. */}
        {showPresetCard && (
          <div className="mt-3 rounded border border-violet-200 dark:border-violet-900/60 bg-violet-50/60 p-2.5">
            {columnPresets.map((preset, i) => (
              <div
                key={preset.label}
                className={[
                  "flex items-start justify-between gap-3",
                  i > 0 ? "mt-2 border-t border-violet-200 dark:border-violet-900/60 pt-2" : "",
                ].join(" ")}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 text-[11.5px] font-semibold text-violet-900 dark:text-violet-200">
                    <span aria-hidden="true">✨</span>
                    <span>Auto-fill with AI</span>
                    <span className="rounded bg-violet-100 dark:bg-violet-950/50 px-1.5 py-px text-[9.5px] font-bold uppercase tracking-wide text-violet-800 dark:text-violet-300 ring-1 ring-violet-200 dark:ring-violet-800">
                      {preset.label}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[11px] leading-snug text-violet-800/90">
                    {preset.description}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => applyPreset(preset)}
                  className="shrink-0 rounded bg-violet-600 px-2.5 py-1 text-[11px] font-medium text-white transition hover:bg-violet-700"
                  title={`Replace the current rule with an AI prompt tuned for ${productType}`}
                >
                  Use AI for this
                </button>
              </div>
            ))}
          </div>
        )}

      </div>
      )}

      {/* Body */}
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        {hideRuleEditor ? (
          <p className="text-[12px] leading-snug text-zinc-600 dark:text-zinc-400">
            This column is filled by the module. Adding a rule here would be
            ignored. Adjust the module configuration on the template instead.
          </p>
        ) : (
          <>
            <RuleBody
              rule={activeRule}
              sourceHeaders={sourceHeaders}
              outputColumns={outputColumns}
              sampleRow={sourceRows[0]}
              onChange={mutate}
              registerFocusedAutocomplete={registerFocusedAutocomplete}
              defaultModels={defaultModels}
              accessibleModels={accessibleModels}
              onRefreshSettings={refreshSettings}
              localClaude={localClaude}
              onDictionariesChanged={onDictionariesChanged}
            />

            <DependencyChips
              column={column}
              rule={activeRule}
              inCycle={cycleColumns.has(column)}
              onSelectColumn={onSelectColumn}
            />
          </>
        )}

        <InlinePreview
          column={column}
          rule={activeRule}
          sourceRows={sourceRows}
          previewRows={previewRows}
          loading={previewLoading}
          dictionaries={dictionaries}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HeaderStatusDot — colour-coded indicator matching the sidebar status mark.
// Stays small (8px) and renders inline with the column-name row.
// ---------------------------------------------------------------------------
function HeaderStatusDot({ status }: { status: ColumnStatus }) {
  if (status.kind === "mapped") {
    const cls = status.isAI ? "bg-violet-500" : "bg-emerald-500";
    return (
      <span
        aria-hidden="true"
        title={status.isAI ? "Mapped (AI)" : "Mapped"}
        className={`inline-block h-2 w-2 shrink-0 rounded-full ${cls}`}
      />
    );
  }
  if (status.kind === "module") {
    const tone = TONE_CLASSES[status.module.tone];
    return (
      <span
        aria-hidden="true"
        title={`Filled by ${status.module.name}`}
        className={`inline-block h-2 w-2 shrink-0 rounded-[2px] ${tone.mark}`}
      />
    );
  }
  if (status.kind === "na") {
    return (
      <span
        aria-hidden="true"
        title="Typically blank on Monjolika's store CSV"
        className="inline-block h-2 w-2 shrink-0 rounded-full ring-1 ring-zinc-300"
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      title="Unset"
      className="inline-block h-2 w-2 shrink-0 rounded-full ring-1 ring-slate-300"
    />
  );
}

// ---------------------------------------------------------------------------
// ModuleBand — surfaces the module that owns this column, with a tone-coloured
// top border, the module's name + glyph, the summary text + bullets, and a
// clear authoritative/override note.
//
// Why this is a card (not a chip): the user needs enough context here to
// understand WHY the column looks "filled" without reading the docs. A summary
// + bullets satisfies that. We deliberately do NOT thread a "Configure module"
// CTA in this wave — the page that owns module config (Profile editor) lives
// behind a different route and the inspector doesn't receive the profile id.
// The summary + the "adjust on the Profile" hint tell the user where to go.
// ---------------------------------------------------------------------------
function ModuleBand({
  module: mod,
  profile,
  authoritative,
}: {
  module: Module;
  profile: Profile;
  authoritative: boolean;
}) {
  const tone = TONE_CLASSES[mod.tone];
  const summary = useMemo(() => mod.summarize(profile), [mod, profile]);

  return (
    <div
      className={[
        "mt-3 rounded border border-t-2 border-zinc-200 dark:border-border p-2.5",
        tone.border,
        tone.cardBg,
      ].join(" ")}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="text-[14px] leading-none"
          >
            {mod.glyph}
          </span>
          <span className={`text-[12px] font-semibold ${tone.title}`}>
            {mod.name}
          </span>
          <span
            className={[
              "rounded px-1 py-px text-[9px] font-semibold uppercase tracking-wide ring-1",
              tone.chip,
            ].join(" ")}
          >
            module
          </span>
        </div>
        {authoritative && (
          <span className="shrink-0 rounded bg-white/70 dark:bg-surface/70 px-1.5 py-px text-[9.5px] font-medium uppercase tracking-wide text-zinc-600 dark:text-zinc-400 ring-1 ring-zinc-200 dark:ring-zinc-700">
            authoritative
          </span>
        )}
      </div>
      <p className={`mt-1.5 text-[11.5px] leading-snug ${tone.body}`}>
        {mod.description}
      </p>
      <p className={`mt-1 text-[11.5px] leading-snug ${tone.body}`}>
        {summary.text}
      </p>
      {summary.bullets && summary.bullets.length > 0 && (
        <ul className={`mt-1 space-y-0.5 text-[10.5px] ${tone.body}`}>
          {summary.bullets.map((b, i) => (
            <li key={i} className="flex items-baseline gap-1">
              <span aria-hidden="true" className="text-zinc-400 dark:text-zinc-500">
                •
              </span>
              <span className="leading-snug">{b}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-2 text-[10.5px] italic text-zinc-500 dark:text-zinc-400">
        {authoritative
          ? "Output is fully controlled by this module. Configure it on the template."
          : "Module fills this column by default. A rule here runs alongside as an override."}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DependencyChips — small inline chip(s) under the rule editor that show:
//  - "↑ depends on: A, B" with clickable deps (navigates inspector), OR
//  - "⚠ Part of cycle" in red if the column participates in a cycle.
//
// Renders nothing when neither applies.
// ---------------------------------------------------------------------------

function DependencyChips({
  column,
  rule,
  inCycle,
  onSelectColumn,
}: {
  column: string;
  rule: Rule;
  inCycle: boolean;
  onSelectColumn?: (column: string) => void;
}) {
  const deps = useMemo(() => extractOutputRefs(rule), [rule]);
  if (inCycle) {
    return (
      <div className="mt-3 inline-flex items-center gap-1 rounded bg-red-50 dark:bg-red-950/40 px-2 py-0.5 text-[10.5px] font-semibold text-red-700 dark:text-red-300 ring-1 ring-red-200">
        ⚠ Part of cycle
      </div>
    );
  }
  if (deps.length === 0) return null;
  return (
    <div className="mt-3 inline-flex flex-wrap items-baseline gap-1 rounded bg-violet-50/60 px-2 py-0.5 text-[10.5px] text-zinc-600 dark:text-zinc-400 ring-1 ring-violet-100">
      <span className="text-violet-700 dark:text-violet-300">↑ depends on:</span>
      {deps.map((d, i) => (
        <span key={d} className="inline-flex items-baseline">
          <button
            type="button"
            onClick={() => onSelectColumn?.(d)}
            title={`Go to ${d}`}
            className="font-mono text-violet-800 dark:text-violet-300 hover:text-violet-950 hover:underline"
          >
            {d}
          </button>
          {i < deps.length - 1 && <span>,&nbsp;</span>}
        </span>
      ))}
      <span className="sr-only">column: {column}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-kind editor body
// ---------------------------------------------------------------------------

function RuleBody({
  rule,
  sourceHeaders,
  outputColumns,
  sampleRow,
  onChange,
  registerFocusedAutocomplete,
  defaultModels,
  accessibleModels,
  onRefreshSettings,
  localClaude,
  onDictionariesChanged,
}: {
  rule: Rule;
  sourceHeaders: string[];
  outputColumns: string[];
  sampleRow: SourceRow | undefined;
  onChange: (r: Rule) => void;
  registerFocusedAutocomplete: (
    handle: FieldInputHandle | null,
  ) => void;
  defaultModels: DefaultModels;
  accessibleModels: AccessibleModelsMap;
  onRefreshSettings: () => void;
  localClaude?: boolean;
  onDictionariesChanged?: () => void;
}) {
  return (
    <ModuleEditorHost
      rule={rule}
      sourceHeaders={sourceHeaders}
      outputColumns={outputColumns}
      sampleRow={sampleRow}
      onChange={onChange}
      registerFocusedAutocomplete={registerFocusedAutocomplete}
      defaultModels={defaultModels}
      accessibleModels={accessibleModels}
      onRefreshSettings={onRefreshSettings}
      localClaude={localClaude}
      onDictionariesChanged={onDictionariesChanged}
      fallback={null}
    />
  );
}

// ---------------------------------------------------------------------------
// Inline preview — three "row N → value" chips
// ---------------------------------------------------------------------------

function InlinePreview({
  column,
  rule,
  sourceRows,
  previewRows,
  loading,
  dictionaries,
}: {
  column: string;
  rule: Rule;
  sourceRows: SourceRow[];
  previewRows: ShopifyRow[];
  loading: boolean;
  dictionaries?: Record<string, LookupDictionary>;
}) {
  // We compute the live preview client-side so it updates instantly while the
  // user is typing (the debounced server preview is shown in the drawer).
  const values = useMemo(() => {
    // AI rules aren't run per keystroke: show what the workbook holds for
    // this column instead (stored AI output, or "pending").
    if (rule.kind === "ai") {
      return previewRows
        .map((row) => row[column] ?? "")
        .filter((value) => value !== "")
        .slice(0, 3);
    }
    const samples = sourceRows.slice(0, 3);
    return samples.map((row) => evalRuleClient(rule, row, dictionaries));
  }, [rule, column, previewRows, sourceRows, dictionaries]);

  // We also surface the server-side value for row 0 (which respects helpers
  // and the full pipeline) when available — useful as a sanity check.
  const serverValue = previewRows[0]?.[column];

  return (
    <div className="mt-5 border-t border-slate-200 dark:border-border pt-3">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
          Live preview
        </span>
        {loading && (
          <span className="text-[10px] text-slate-400 dark:text-slate-500">updating…</span>
        )}
      </div>
      <ul className="mt-1.5 space-y-1 rounded border border-slate-200 dark:border-border bg-slate-50 dark:bg-background p-2">
        {values.length === 0 && (
          <li className="font-mono text-[11px] text-slate-400 dark:text-slate-500">
            {rule.kind === "ai" ? "No workbook rows yet." : "No source rows loaded."}
          </li>
        )}
        {values.map((v, i) => (
          <li key={i} className="flex items-baseline gap-2">
            <span className="w-12 shrink-0 text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500">
              row {i + 1}
            </span>
            <span className="min-w-0 flex-1 truncate font-mono text-[11.5px] text-slate-800 dark:text-slate-200" title={v}>
              {v === AI_RULE_PLACEHOLDER ? (
                <AIPendingChip />
              ) : v === "" ? (
                <span className="text-slate-300 dark:text-slate-600">Blank</span>
              ) : (
                v
              )}
            </span>
          </li>
        ))}
      </ul>
      {rule.kind !== "ai" && serverValue !== undefined && serverValue !== values[0] && (
        <div className="mt-1.5 text-[10.5px] text-slate-500 dark:text-slate-400">
          Server output (after variants/images):{" "}
          {serverValue === AI_RULE_PLACEHOLDER ? (
            <AIPendingChip />
          ) : (
            <span className="font-mono">{serverValue || "Blank"}</span>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Client-side evaluator for inline preview only.
// ---------------------------------------------------------------------------

function evalRuleClient(
  rule: Rule,
  row: SourceRow | undefined,
  dictionaries?: Record<string, LookupDictionary>,
): string {
  if (!row) return "";
  switch (rule.kind) {
    case "source":
      return shortString(row[rule.column]);
    case "static":
      // static may reference fields — resolve segments (or a legacy string).
      return resolveTemplate(rule.value, { row });
    case "template":
      return resolveTemplate(rule.value, { row });
    case "computed":
      // Same engine as the export (smart slugify, number helpers, …).
      return cellModule(rule.kind)?.resolve(rule, { row, helpers: defaultHelpers }) ?? "";
    case "dictionary":
      return cellModule(rule.kind)?.resolve(rule, { row, helpers: defaultHelpers, dictionaries }) ?? "";
    case "find-replace":
      return cellModule(rule.kind)?.resolve(rule, { row, helpers: defaultHelpers, dictionaries }) ?? "";
    case "expression":
      return cellModule(rule.kind)?.resolve(rule, { row, helpers: defaultHelpers, dictionaries }) ?? "";
    case "conditional":
      return cellModule(rule.kind)?.resolve(rule, { row, helpers: defaultHelpers, dictionaries }) ?? "";
    case "ai":
      // No live preview for AI rules — would burn tokens on every keystroke.
      // We emit the placeholder sentinel; the renderer swaps it for a
      // violet "not generated yet" chip so the affordance is obvious.
      return AI_RULE_PLACEHOLDER;
    case "dropbox-image":
      return cellModule(rule.kind)?.resolve(rule, { row, helpers: defaultHelpers }) ?? "";
    default: {
      // Any other module kind (price-engine, javascript, and future modules)
      // resolves generically through its registry descriptor. resolve() is pure
      // and never throws, so a misbehaving module yields a blank cell instead of
      // breaking the whole preview — node-style fault isolation.
      const r: Rule = rule;
      return cellModule(r.kind)?.resolve(r, { row, helpers: defaultHelpers, dictionaries }) ?? "";
    }
  }
}

/**
 * Small inline chip rendered in place of an AI-rule cell's value when the
 * preview/server pass left the placeholder sentinel ( AI_PENDING ) intact.
 * Tells the user the cell hasn't been generated yet and how to generate it.
 */
function AIPendingChip() {
  return (
    <span
      className="inline-flex items-center gap-1 rounded bg-violet-50 dark:bg-violet-950/40 px-1.5 py-0.5 font-mono text-[10.5px] font-medium text-violet-700 dark:text-violet-300 ring-1 ring-violet-200 dark:ring-violet-800"
      title="Not generated yet. Click ▶ on the column header (or Generate AI in the bottom bar); the output is saved in the workbook."
    >
      <span aria-hidden>✨</span>
      Not generated yet, click ▶ on the column
    </span>
  );
}
